/** Enum-like constants for the upload dialog (#2518). */

/** Upload sources the backend can enable — the `opts.providers` values from open-api upload options.
 *  Declared in display order: the legacy Uppy modal's tab order, after My Device. */
export const UPLOAD_SOURCE = {
  WEBCAM: 'webcam',
  GOOGLE_DRIVE: 'google-drive',
  DROPBOX: 'dropbox',
  ONEDRIVE: 'onedrive',
} as const;
export type UploadSource = (typeof UPLOAD_SOURCE)[keyof typeof UPLOAD_SOURCE];

/** Uppy plugin id of each source — what Dashboard's `showPanel` expects. */
export const UPLOAD_SOURCE_PLUGIN_ID: Record<UploadSource, string> = {
  [UPLOAD_SOURCE.WEBCAM]: 'Webcam',
  [UPLOAD_SOURCE.GOOGLE_DRIVE]: 'GoogleDrive',
  [UPLOAD_SOURCE.DROPBOX]: 'Dropbox',
  [UPLOAD_SOURCE.ONEDRIVE]: 'OneDrive',
};

/** Why a file was refused (shown on its row in the dialog's list). */
export const REJECTION_REASON = {
  TOO_MANY: 'tooMany',
  WRONG_TYPE: 'wrongType',
  TOO_LARGE: 'tooLarge',
  TOO_SMALL: 'tooSmall',
  EMPTY: 'empty',
  RESOLUTION: 'resolution',
  BITRATE: 'bitrate',
  DUPLICATE: 'duplicate',
  OTHER: 'other',
} as const;
export type RejectionReason = (typeof REJECTION_REASON)[keyof typeof REJECTION_REASON];

/** State of one file in the dialog's list. */
export const UPLOAD_FILE_STATUS = {
  READY: 'ready',
  UPLOADING: 'uploading',
  /** Uploaded; the server is still processing it (Replace Thumbnail waits for encoding). */
  PROCESSING: 'processing',
  DONE: 'done',
  ERROR: 'error',
} as const;
export type UploadFileStatus = (typeof UPLOAD_FILE_STATUS)[keyof typeof UPLOAD_FILE_STATUS];

/** File extensions per asset type, for files the upload dialog only knows by name (refused
 *  files never reach Uppy, so they have no MIME type). Lower case, no dot. */
export const UPLOAD_IMAGE_EXTENSIONS: readonly string[] = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'heic',
  'tif',
  'tiff',
  'avif',
];
export const UPLOAD_VIDEO_EXTENSIONS: readonly string[] = [
  'mp4',
  'mov',
  'm4v',
  'webm',
  'avi',
  'mkv',
  'wmv',
];
export const UPLOAD_AUDIO_EXTENSIONS: readonly string[] = ['mp3'];
export const UPLOAD_DOCUMENT_EXTENSIONS: readonly string[] = [
  'pdf',
  'txt',
  'md',
  'csv',
  'rtf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'xls',
  'xlsx',
];

/** Build flag for asset upload and Replace Thumbnail. Default ON: only an explicit "false" falls
 *  back to Uppy's own modal (rollback without a code change). */
export const USE_NATIVE_UPLOAD_DIALOG = import.meta.env.VITE_USE_NATIVE_UPLOAD_DIALOG !== 'false';
